import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

// A month counts as "filled in" once at least one metric carries a value.
function hasAnyValue(vals) {
  return Object.values(vals || {}).some((v) => v !== '' && v !== null && v !== undefined);
}

// In-report paid-advertising data entry. Shown inside SiteReports for the
// selected month. Renders one card per enabled channel; each metric has a
// "show in report" checkbox + a value input. Saves to paid_ads_metrics, which
// is the single source of truth for both the client report and the module.
export default function PaidAdsEntry({ siteId, month, step, onStatusChange }) {
  const [channels, setChannels] = useState([]);
  const [values, setValues] = useState({});   // {platform: {key: val}}
  const [display, setDisplay] = useState({});  // {platform: [keys]}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Report "does this month have data?" upward (SiteReports gates report creation
  // on it). Held in a ref so an inline callback from the parent can't retrigger load().
  const statusRef = useRef(onStatusChange);
  useEffect(() => { statusRef.current = onStatusChange; });
  const report = useCallback((has) => {
    if (statusRef.current) statusRef.current(has);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setMsg(null);
    api.getSitePaidAds(siteId, month)
      .then((d) => {
        const chans = d.channels || [];
        setChannels(chans);
        const v = {}, disp = {};
        chans.forEach((c) => { v[c.platform] = { ...(c.values || {}) }; disp[c.platform] = [...(c.display || [])]; });
        setValues(v);
        setDisplay(disp);
        // No channels configured means there is nothing to fill in — never block.
        report(!chans.length || chans.some((c) => hasAnyValue(c.values)));
      })
      .catch(() => { setChannels([]); report(true); })
      .finally(() => setLoading(false));
  }, [siteId, month, report]);

  useEffect(() => { load(); }, [load]);

  function setValue(platform, key, raw) {
    setValues((prev) => ({ ...prev, [platform]: { ...(prev[platform] || {}), [key]: raw } }));
  }

  function toggleDisplay(platform, key) {
    setDisplay((prev) => {
      const cur = prev[platform] || [];
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      return { ...prev, [platform]: next };
    });
  }

  function handleSave() {
    setSaving(true);
    setMsg(null);
    const payloads = channels.map((c) => {
      // drop empty-string values so the report/module treat them as "not entered"
      const cleaned = {};
      Object.entries(values[c.platform] || {}).forEach(([k, val]) => {
        if (val !== '' && val !== null && val !== undefined) cleaned[k] = Number(val);
      });
      return { platform: c.platform, values: cleaned, display: display[c.platform] || [] };
    });
    Promise.all(payloads.map((p) => api.savePaidAds(siteId, month, p)))
      .then(() => {
        setMsg({ type: 'success', text: 'נתוני הפרסום נשמרו' });
        // Reflect the new state upward right away, so the "create report" gate
        // opens without needing a reload.
        report(!payloads.length || payloads.some((p) => hasAnyValue(p.values)));
      })
      .catch((err) => setMsg({ type: 'error', text: 'שגיאה: ' + err.message }))
      .finally(() => { setSaving(false); setTimeout(() => setMsg(null), 3000); });
  }

  const cardStyle = { padding: '20px', marginTop: '16px' };
  const suffix = { currency: '₪', percent: '%', number: '' };

  if (loading) {
    return (
      <div className="section-card" style={cardStyle}>
        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>טוען נתוני פרסום...</div>
      </div>
    );
  }

  if (!channels.length) {
    return (
      <div className="section-card" style={cardStyle}>
        <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '6px' }}>פרסום ממומן</div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          לא הוגדרו ערוצי פרסום לאתר זה. יש להפעיל ערוצים בהגדרות האתר כדי להזין נתונים.
        </div>
      </div>
    );
  }

  return (
    <div className="section-card" style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {step != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0, background: 'var(--accent)', color: '#fff', fontSize: '13px', fontWeight: 700 }}>{step}</span>
            )}
            <div style={{ fontWeight: 700, fontSize: '16px' }}>נתונים חודשיים</div>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px', paddingRight: step != null ? '34px' : 0 }}>
            סמן אילו מדדים יוצגו בדוח הלקוח והזן את הערכים. הנתונים נשמרים ומוצגים גם במודול "תוצאות עסקיות".
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '8px 16px' }}>
          {saving ? 'שומר...' : 'שמור נתוני פרסום'}
        </button>
      </div>

      {msg && (
        <div style={{
          padding: '8px 12px', borderRadius: '8px', margin: '10px 0', fontSize: '13px',
          background: msg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
          color: msg.type === 'success' ? 'var(--green)' : 'var(--red)',
        }}>{msg.text}</div>
      )}

      {channels.map((c, idx) => (
        <React.Fragment key={c.platform}>
          {(idx === 0 || channels[idx - 1].kind !== c.kind) && (
            <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--accent)', marginTop: '16px' }}>
              {c.kind === 'business' ? 'תוצאות עסקיות' : 'פרסום ממומן'}
            </div>
          )}
          <div style={{ border: '1px solid var(--border)', borderRadius: '10px', padding: '14px', marginTop: '10px' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '10px' }}>{c.label}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
            {(c.metrics || []).map((m) => {
              const checked = (display[c.platform] || []).includes(m.key);
              const inputId = 'metric-' + c.platform + '-' + m.key;
              return (
                // Label sits above the input, not beside it: metric labels are free
                // text typed by the user in site settings and often run long (e.g.
                // "לחיצות על כפתור וואצטאפ (לפי אנליטיקס)"), which squeezed the number
                // field down to a few characters in the old side-by-side row.
                <div key={m.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: '6px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <input type="checkbox" checked={checked} onChange={() => toggleDisplay(c.platform, m.key)} title="הצג בדוח" style={{ marginTop: '3px', flexShrink: 0 }} />
                    <label htmlFor={inputId} style={{ flex: 1, fontSize: '13px', lineHeight: 1.4, color: 'var(--text-secondary)', cursor: 'pointer' }}>{m.label}</label>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      id={inputId}
                      type="number"
                      value={(values[c.platform] && values[c.platform][m.key] != null) ? values[c.platform][m.key] : ''}
                      onChange={(e) => setValue(c.platform, m.key, e.target.value)}
                      style={{
                        width: '100%', boxSizing: 'border-box', padding: '6px 10px',
                        paddingLeft: suffix[m.type] ? '22px' : '10px',
                        borderRadius: '6px', border: '1px solid var(--border)',
                        background: 'var(--surface)', color: 'var(--text)', fontSize: '13px', direction: 'ltr',
                      }}
                    />
                    {suffix[m.type] && (
                      <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--text-muted)' }}>{suffix[m.type]}</span>
                    )}
                  </div>
                </div>
              );
            })}
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}
